// InsufficientPlanModal.jsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { playVoice } from '../../utils/voicePlayer';

const renderTextWithBold = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <span key={index} className="text-white font-semibold">{part.slice(2, -2)}</span>;
        }
        return part;
    });
};

const InsufficientPlanModal = ({ onClose, requiredPlan = 'Pro', permission }) => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const isEn = language === 'en';

    useEffect(() => {
        playVoice('payment-request');
    }, []);

    const handleUpgrade = () => {
        onClose();
        navigate('/account');
    };

    const getPlanDescription = () => {
        let desc = t('featureRequiresPlan', { plan: requiredPlan });
        if (desc) {
            desc = desc.replace(/Higher Tier/gi, isEn ? 'a higher tier' : 'более высокий тариф');
        }
        return desc;
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="select-text"
            >
                {/* ═══ Header ═══ */}
                <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-white/[0.08]">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 stroke-white fill-none shrink-0" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <h3 className="text-[1.05rem] font-bold text-white tracking-tight leading-none">
                            {t('upsellRequired') || (isEn ? 'Upgrade Required' : 'Требуется обновление тарифа')}
                        </h3>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="w-7 h-7 rounded-full bg-white/[0.04] hover:bg-white/[0.1] text-white/50 hover:text-white flex items-center justify-center transition-colors active:scale-90 cursor-pointer shrink-0 ml-2"
                    >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* ═══ Description ═══ */}
                <div className="text-neutral-300 text-xs sm:text-sm leading-relaxed mb-4 space-y-2">
                    {renderTextWithBold(getPlanDescription())}
                </div>

                {/* ═══ Permission Context (if specified) ═══ */}
                {permission && (
                    <div className="p-2.5 sm:p-3 rounded-xl bg-white/[0.03] border border-white/[0.08] mb-4 flex items-start gap-2.5">
                        <svg className="w-4 h-4 stroke-white/50 fill-none mt-0.5 shrink-0" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        <p className="text-xs text-neutral-300/80 leading-relaxed">
                            {t('perm_' + permission.replace(/\./g, '_'), { defaultValue: permission })}
                        </p>
                    </div>
                )}

                {/* ═══ Action Buttons ═══ */}
                <div className="flex items-center gap-2 pt-1">
                    <button
                        type="button"
                        onClick={handleUpgrade}
                        className="flex-1 min-h-[42px] py-2.5 px-3 sm:px-4 rounded-xl bg-white text-black hover:bg-neutral-200 font-bold text-xs sm:text-sm shadow-[0_4px_20px_rgba(255,255,255,0.2)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                        <svg className="w-3.5 h-3.5 fill-current shrink-0" viewBox="0 0 24 24">
                            <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5m14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
                        </svg>
                        <span className="truncate">{t('upgradePlan') || (isEn ? 'Upgrade Plan' : 'Обновить тариф')}</span>
                    </button>

                    <button
                        type="button"
                        onClick={onClose}
                        className="min-h-[42px] py-2.5 px-3.5 sm:px-5 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-white/75 hover:text-white font-semibold text-xs sm:text-sm active:scale-[0.98] transition-all cursor-pointer shrink-0"
                    >
                        {t('cancel') || (isEn ? 'Cancel' : 'Отмена')}
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default InsufficientPlanModal;